import { captureException } from '@sentry/browser';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import first from 'lodash/first';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTopicSelectionGroupsQuery } from 'src/modules/user/documents/topicSelectionGroupsQuery.generated';
import { WellbeingReportHeader } from 'src/modules/assessments/components/WellbeingReportHeader';
import { font } from 'src/shared/styles/variables';
import { colors } from '@modernhealth/vital-foundation';

import {
    HeadingText10,
    BodyTextSmall,
    ComponentTextSmall as NoSubtopicText,
    pxToRem,
} from '@modernhealth/vital-components';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    margin: '0 auto',
});

const TopicsWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
});

const Topic = styled.div({
    display: 'flex',
    flexDirection: 'column',
    margin: '16px 8px',
});

const TopicContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    borderBottom: `2px solid ${colors.gray05}`,
    alignItems: 'center',
    '&:last-child': {
        borderBottom: 'hidden',
    },
});

const SecondaryTextLink = styled.span({
    cursor: 'pointer',
    marginLeft: pxToRem(12),
    textDecoration: 'underline',
    color: colors.blue55,
});

const ImageWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: pxToRem(48),
    width: pxToRem(106),
    marginTop: pxToRem(16),
    marginBottom: pxToRem(16),
    minWidth: pxToRem(106),
});

const TopicImage = styled.img({
    height: '100%',
    objectFit: 'contain',
});

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
     }
`;

const BlinkingWrapper = styled.span({
    animation: `${fadeIn} ease infinite 1.6s`,
    ...font.medium(font.size.base),
});

export enum TopicSelectionTestID {
    Topics = 'TopicSelectionTestID.Topics',
    EditOrSelectTopicsLink = 'TopicSelectionTestID.EditOrSelectTopicsLink',
    NoTopics = 'TopicSelectionTestID.NoTopics',
}

interface Props {
    loadingText?: string;
    handleUpdateTopics: () => void;
}

export const TopicSelection: React.FC<Props> = ({ loadingText, handleUpdateTopics }) => {
    const { t } = useTranslation();
    const { data, refetch, loading, error } = useTopicSelectionGroupsQuery({
        notifyOnNetworkStatusChange: true,
    });

    useEffect(() => {
        if (error) {
            captureException(new Error('An unexpected topicSelectionGroups query error was found'));
        }
    }, [error]);

    const topicsOfFocus = first(data?.topicSelectionGroups);
    const lastUpdatedOn = first(topicsOfFocus?.subtopicSelections)?.createdAt;

    const subTopicsOfFocus = topicsOfFocus?.subtopicSelections?.map(item => {
        return {
            name: item?.subtopicOfFocus?.name,
            createdAt: item?.createdAt,
            topicOfFocus: item?.subtopicOfFocus?.topicOfFocus?.name,
            image: item?.subtopicOfFocus?.topicOfFocus?.image,
        };
    });

    const hasSubtopicsOfFocus = subTopicsOfFocus && subTopicsOfFocus?.length >= 1;
    const changeOrSelectTopics = hasSubtopicsOfFocus ? 'edit' : 'select';

    useEffect(() => {
        refetch();
    }, []);

    if (error || !data) {
        return null;
    }

    return (
        <Wrapper>
            <WellbeingReportHeader title={t('assessments:wellbeing_report.topics')} />
            <TopicsWrapper>
                {loading && <BlinkingWrapper>{loadingText}</BlinkingWrapper>}
                {!loading && (
                    <>
                        {hasSubtopicsOfFocus ? (
                            <>
                                <BodyTextSmall marginBottom={24} fontColor="gray65">
                                    {t('assessments:wellbeing_report.topics.description')}
                                </BodyTextSmall>
                                <BodyTextSmall marginBottom={8} fontColor="gray65">
                                    {t('assessments:wellbeing_report.topics.last_updated_on', {
                                        date: moment(lastUpdatedOn).format('MMM D, YYYY'),
                                    })}
                                    <SecondaryTextLink onClick={handleUpdateTopics}>
                                        {t(
                                            `assessments:wellbeing_report.${changeOrSelectTopics}_topics`,
                                        )}
                                    </SecondaryTextLink>
                                </BodyTextSmall>
                                {subTopicsOfFocus?.map(
                                    ({ name, createdAt, topicOfFocus, image }, idx) => (
                                        <TopicContainer
                                            key={`${topicOfFocus}-${name}-${createdAt}`}
                                        >
                                            <ImageWrapper>
                                                <TopicImage src={image} />
                                            </ImageWrapper>
                                            <Topic
                                                key={`${idx}-${name}+${createdAt}`}
                                                data-testid={TopicSelectionTestID.Topics}
                                            >
                                                <HeadingText10 marginBottom={0} maxWidth="356px">
                                                    {topicOfFocus}
                                                </HeadingText10>
                                                <BodyTextSmall fontColor="gray65">
                                                    {name}
                                                </BodyTextSmall>
                                            </Topic>
                                        </TopicContainer>
                                    ),
                                )}
                            </>
                        ) : (
                            <NoSubtopicText
                                testID={TopicSelectionTestID.NoTopics}
                                fontColor="gray65"
                            >
                                {t('assessments:wellbeing_report.no_subtopics_fallback_text')}
                                <SecondaryTextLink onClick={handleUpdateTopics}>
                                    {t(
                                        `assessments:wellbeing_report.${changeOrSelectTopics}_topics`,
                                    )}
                                </SecondaryTextLink>
                            </NoSubtopicText>
                        )}
                    </>
                )}
            </TopicsWrapper>
        </Wrapper>
    );
};
